import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { cn } from '../../lib/cn.ts'
import { useSidebar } from '../../hooks/useSidebar.ts'
import { Sidebar } from './Sidebar/Sidebar.tsx'
import { Header } from './Header/Header.tsx'
import type { MainLayoutProps, NavItem } from './types.ts'

const defaultResolve = (item: NavItem) => item.path

export function MainLayout({
  navItems,
  brand,
  headerRight,
  sidebarFooter,
  resolvePath = defaultResolve,
  children,
}: MainLayoutProps) {
  const { isCollapsed, toggle, mobileOpen, openMobile, closeMobile } = useSidebar()
  const containerRef = useRef<HTMLDivElement>(null)
  const { pathname } = useLocation()

  useEffect(() => {
    if (containerRef.current) containerRef.current.scrollTop = 0
    // Navigating from inside the mobile drawer closes it.
    closeMobile()
  }, [pathname, closeMobile])

  // While the mobile drawer is open: Escape closes it and the body is scroll-
  // locked. Mirrors the Dialog primitive's convention (no focus-trap lib).
  useEffect(() => {
    if (!mobileOpen) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeMobile()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [mobileOpen, closeMobile])

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gradient-brand text-foreground">
      {/* Backdrop — mobile only, only while the drawer is open. Sits below the
          sidebar (z-40), above content. */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/60 md:hidden"
          aria-hidden="true"
          onClick={closeMobile}
        />
      )}

      <Sidebar
        isCollapsed={isCollapsed}
        onToggle={toggle}
        mobileOpen={mobileOpen}
        items={navItems}
        brand={brand}
        footer={sidebarFooter}
        resolvePath={resolvePath}
      />

      <Header sidebarCollapsed={isCollapsed} onOpenMobile={openMobile} right={headerRight} />

      <main
        className={cn(
          // Symmetric gutters on mobile; at md+ the sidebar margin provides the
          // left offset and the right keeps its original pr-6 (no pl).
          'flex-1 overflow-hidden pb-5 pl-4 pr-4 md:pl-0 md:pr-6',
          'transition-[margin-left] duration-300 ease-in-out',
          // Full-width on mobile; the sidebar collapse margin only applies at md+.
          'ml-0',
          isCollapsed
            ? 'md:ml-[var(--sidebar-width-collapsed)]'
            : 'md:ml-[var(--sidebar-width-expanded)]',
        )}
      >
        <div
          ref={containerRef}
          className="h-full overflow-y-auto rounded-lg border border-border/60 bg-background-app p-4 sm:p-6"
        >
          {children}
        </div>
      </main>
    </div>
  )
}
